// Lightweight emoji picker. Hand-curated set of common emojis grouped into
// a handful of categories. No external picker library — those weigh
// hundreds of KB and bring fonts. This is ~90 emoji, ~3 KB of source.

import { useEffect, useRef } from 'react';
import styles from './EmojiPicker.module.css';

interface Group {
  label: string;
  emojis: string[];
}

const GROUPS: Group[] = [
  {
    label: 'Smileys',
    emojis: ['😀', '😄', '😅', '😂', '🙂', '😊', '😉', '😌', '😍', '😘', '😎', '🤔', '🙃', '😴', '😢', '😭', '😡', '😱', '🤯', '🤗'],
  },
  {
    label: 'Hands & people',
    emojis: ['👋', '👍', '👎', '👌', '🙏', '👏', '💪', '🤝', '✌️', '🙌', '👀', '🧠', '🫡'],
  },
  {
    label: 'Hearts & marks',
    emojis: ['❤️', '🧡', '💛', '💚', '💙', '💜', '🖤', '💔', '✅', '❌', '⭐', '🔥', '✨', '💯', '⚡', '☑️'],
  },
  {
    label: 'Objects',
    emojis: ['💻', '📱', '⌨️', '🖥️', '📎', '📌', '📝', '📅', '📷', '🎉', '🎁', '☕', '🍕', '🚀', '🛠️', '🔧', '🧰'],
  },
  {
    label: 'Symbols',
    emojis: ['➡️', '⬅️', '⬆️', '⬇️', '🔁', '🔄', '⏰', '⏳', '💡', '❓', '❗', '💬', '🔒', '🔓', '🔔'],
  },
];

interface Props {
  onPick: (emoji: string) => void;
  onClose: () => void;
}

export function EmojiPicker({ onPick, onClose }: Props) {
  const ref = useRef<HTMLDivElement | null>(null);

  // Close on outside click + Esc. Capture-phase so the listener fires
  // before any other component swallows the event.
  useEffect(() => {
    function onDocClick(e: MouseEvent) {
      if (ref.current?.contains(e.target as Node)) return;
      onClose();
    }
    function onKey(e: KeyboardEvent) {
      if (e.key === 'Escape') onClose();
    }
    document.addEventListener('mousedown', onDocClick);
    window.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('mousedown', onDocClick);
      window.removeEventListener('keydown', onKey);
    };
  }, [onClose]);

  return (
    <div ref={ref} className={styles.panel} role="dialog" aria-label="Emoji picker">
      {GROUPS.map((g) => (
        <section key={g.label} className={styles.group}>
          <header className={styles.groupLabel}>{g.label}</header>
          <div className={styles.grid}>
            {g.emojis.map((e) => (
              <button
                key={e}
                type="button"
                className={styles.cell}
                onClick={() => {
                  onPick(e);
                }}
                aria-label={`Insert ${e}`}
              >
                {e}
              </button>
            ))}
          </div>
        </section>
      ))}
    </div>
  );
}
